import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography, DialogActions, DialogContent, CircularProgress } from '@mui/material';
import { Image as ImageIcon, Description as FileIcon } from '@mui/icons-material';
import { Attachment } from '../../_types/manuals/draft';
import useManageAttachment from '../../DraftManuals/_data_sources/useManageAttachment';
import Modal from 'components/_layouts/Modal';
import FileUploader, { FileInfo } from 'components/_atoms/FileUploader';
import { translate as t } from 'hooks/useTranslations';
import AttachmentPreviewBox from './AttachmentPreviewBox';

interface AttachmentSelectorInlineProps {
  manualId: string;
  attachmentId?: string;
  onSelectAttachment: (attachmentId: string) => void;
  label?: string;
  acceptImageOnly?: boolean;
  attachments?: Attachment[];
  onAttachmentUploaded?: (newAttachment: Attachment) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

const AttachmentSelectorInline: React.FC<AttachmentSelectorInlineProps> = ({
  manualId,
  attachmentId,
  onSelectAttachment,
  label = 'Attachment',
  acceptImageOnly = false,
  attachments,
  onAttachmentUploaded,
  onLoadingChange,
}) => {
  const { getAttachment, createAttachment, base64ToBlob, uploadFile } = useManageAttachment();
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (attachmentId && manualId) {
      setIsLoading(true);
      onLoadingChange?.(true);
      getAttachment({ manualId, attachmentId })
        .then((response) => {
          setAttachment(response as any);
        })
        .catch((error) => {
          console.error('Failed to fetch attachment:', error);
          setAttachment(null);
        })
        .finally(() => {
          setIsLoading(false);
          onLoadingChange?.(false);
        });

    } else {
      setAttachment(null);
      onLoadingChange?.(false);
    }
  }, [attachmentId, manualId]);

  const handleRemove = () => {
    setAttachment(null);
    onSelectAttachment('');
  };

  const handleSelectFromLibrary = async (selectedId: string) => {
    setShowLibrary(false);
    setIsLoading(true);
    onLoadingChange?.(true);

    try {
      const response = await getAttachment({ manualId, attachmentId: selectedId });
      const fetchedAttachment = response as any;
      setAttachment(fetchedAttachment);
      onSelectAttachment(selectedId);
    } catch (error) {
      console.error('Failed to fetch attachment:', error);
      setAttachment(null);
    } finally {
      setIsLoading(false);
      onLoadingChange?.(false);
    }
  };

  const handleFileChange = (info: FileInfo) => {
    setFileInfo(info);
  };

  const createAttachmentAsync = (params: {
    manualId: string;
    file: File;
    name: string;
    size: number;
    globalAttachmentId: string;
    jurisdictionAttachmentId: string;
  }): Promise<{ url: string; id: string; }> => {
    return new Promise((resolve, reject) => {
      createAttachment(
        params,
        (url, id) => resolve({ url, id }),
        (err) => reject(err)
      );
    });
  };

  const handleUploadConfirm = async () => {
    if (!fileInfo || !manualId) return;

    setIsUploading(true);
    onLoadingChange?.(true);

    try {
      const { url: uploadUrl, id: newAttachmentId } = await createAttachmentAsync({
        manualId,
        file: new File([fileInfo.data], fileInfo.name, { type: fileInfo.type }),
        name: fileInfo.name,
        size: fileInfo.size,
        globalAttachmentId: '',
        jurisdictionAttachmentId: '',
      });

      const blob = base64ToBlob(fileInfo.data, fileInfo.type);
      const file = new File([blob], fileInfo.name, { type: fileInfo.type });
      await uploadFile(uploadUrl, file);

      const response = await getAttachment({ manualId, attachmentId: newAttachmentId });
      const newAttachment = response as any;
      setAttachment(newAttachment);
      onSelectAttachment(newAttachmentId);

      setShowUploadDialog(false);
      setFileInfo(null);

      if (onAttachmentUploaded) {
        onAttachmentUploaded(newAttachment);
      }
    } catch (err) {
      console.error('Error in lock/create/upload flow:', err);
    } finally {
      setIsUploading(false);
      onLoadingChange?.(false);
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (['png', 'jpg', 'jpeg'].includes(ext)) {
      return <ImageIcon fontSize='small' />;
    }
    return <FileIcon fontSize='small' />;
  };

  const formatFileSize = (sizeStr: string) => {
    const sizeVal = parseInt(sizeStr, 10) || 0;
    return (sizeVal / 1024).toFixed(1) + ' KB';
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (attachment) {
    return (
      <>
        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <AttachmentPreviewBox attachment={attachment} isLoading={isLoading} />
          <Stack sx={{ flex: 1 }}>
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              {attachment.name}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {formatFileSize(attachment.size)}
            </Typography>
          </Stack>

          <Stack direction='row' spacing={1}>
            <Button size='small' onClick={() => setShowLibrary(true)}>
              {t('common.change')}
            </Button>
            <Button size='small' color='error' onClick={handleRemove}>
              {t('common.remove')}
            </Button>
          </Stack>
        </Box>

        <Modal
          open={showLibrary}
          onClose={() => setShowLibrary(false)}
          title={t('draftManuals.attachmentLibrary.selectAttachment')}
          size='sm'
          disableRestoreFocus={true}
        >
          <DialogContent>
            <Stack spacing={1}>
              {attachments && attachments.length > 0 ? (
                attachments
                  .filter(att => {
                    if (!acceptImageOnly) return true;
                    const ext = att.name.split('.').pop()?.toLowerCase() || '';
                    return ['png', 'jpg', 'jpeg'].includes(ext);
                  })
                  .map(att => (
                    <Box
                      key={att.id}
                      onClick={() => handleSelectFromLibrary(att.id)}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <Stack direction='row' spacing={2} alignItems='center'>
                        {getFileIcon(att.name)}
                        <Stack sx={{ flex: 1 }}>
                          <Typography variant='body2'>{att.name}</Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {formatFileSize(att.size)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  ))
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  {t('draftManuals.attachmentLibrary.noAttachmentsAvailable')}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLibrary(false)}>{t('common.cancel')}</Button>
          </DialogActions>
        </Modal>
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <Typography variant='body2' color='text.secondary'>
          {t('draftManuals.attachmentLibrary.noAttachmentSelected', undefined, { label: label.toLowerCase() })}
        </Typography>
        <Stack direction='row' spacing={1}>
          <Button size='small' variant='outlined' onClick={() => setShowLibrary(true)}>
            {t('common.select')}
          </Button>
          <Button size='small' variant='contained' onClick={() => setShowUploadDialog(true)}>
            {t('common.upload')}
          </Button>
        </Stack>
      </Box>

      <Modal
        open={showLibrary}
        onClose={() => setShowLibrary(false)}
        title={t('draftManuals.attachmentLibrary.selectAttachment')}
        size='sm'
        disableRestoreFocus={true}
      >
        <DialogContent>
          <Stack spacing={1}>
            {attachments && attachments.length > 0 ? (
              attachments
                .filter(att => {
                  if (!acceptImageOnly) return true;
                  const ext = att.name.split('.').pop()?.toLowerCase() || '';
                  return ['png', 'jpg', 'jpeg'].includes(ext);
                })
                .map(att => (
                  <Box
                    key={att.id}
                    onClick={() => handleSelectFromLibrary(att.id)}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Stack direction='row' spacing={2} alignItems='center'>
                      {getFileIcon(att.name)}
                      <Stack sx={{ flex: 1 }}>
                        <Typography variant='body2'>{att.name}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {formatFileSize(att.size)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                ))
            ) : (
              <Typography variant='body2' color='text.secondary'>
                {t('draftManuals.attachmentLibrary.noAttachmentsAvailable')}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLibrary(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Modal>

      <Modal
        open={showUploadDialog}
        onClose={() => {
          setShowUploadDialog(false);
          setFileInfo(null);
        }}
        title={t('draftManuals.attachmentLibrary.addDialogTitle')}
        size='sm'
        disableRestoreFocus={true}
      >
        <DialogContent>
          <Stack spacing={2}>
            <FileUploader
              onChange={handleFileChange}
              onError={(errors) => {
                if (errors.length > 0) {
                  console.error('File upload errors:', errors);
                }
              }}
              placeholder={t('draftManuals.attachmentLibrary.fileUploaderPlaceholder')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color='neutral'
            onClick={() => {
              setShowUploadDialog(false);
              setFileInfo(null);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant='contained'
            disabled={!fileInfo || isUploading}
            onClick={handleUploadConfirm}
            endIcon={isUploading ? <CircularProgress size={16} /> : null}
          >
            {isUploading ? t('draftManuals.attachmentLibrary.uploading') : t('draftManuals.attachmentLibrary.upload')}
          </Button>
        </DialogActions>
      </Modal>
    </>
  );
};

export default AttachmentSelectorInline;
